import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Button,
  Table,
  Box,
  SpaceBetween,
  Badge,
  Modal,
  FormField,
  Input,
  Select,
  Alert,
  Toggle,
  Container
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';
import HierarchicalTitleView from '../components/HierarchicalTitleView';

const TitleList = () => {
  const navigate = useNavigate();
  const [titles, setTitles] = useState([]);
  const [filteredTitles, setFilteredTitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  const [imdbId, setImdbId] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState('');
  const [hierarchicalView, setHierarchicalView] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState(null);

  const contentTypeOptions = [
    { label: 'All Types', value: '' },
    { label: 'Series', value: 'series' },
    { label: 'Film', value: 'film' },
    { label: 'Documentary', value: 'documentary' },
    { label: 'Episode', value: 'episode' },
    { label: 'Season', value: 'season' },
    { label: 'Special', value: 'special' },
  ];

  const loadTitles = async () => {
    setLoading(true);
    try {
      const response = await titleAPI.listTitles({ limit: 500 });
      const items = response.data.items || [];
      setTitles(items);
      applyFilters(items, searchQuery, filterType);
    } catch (error) {
      setError('Failed to load titles');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (items, query, type) => {
    let result = items;
    
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(t => {
        const title = (t.metadata?.title || '').toLowerCase();
        const desc = (t.metadata?.synopsis || t.metadata?.description || '').toLowerCase();
        const genre = (t.metadata?.genre || []).join(' ').toLowerCase();
        return title.includes(q) || desc.includes(q) || genre.includes(q);
      });
    }
    
    if (type && type.value) {
      result = result.filter(t => t.content_type === type.value);
    }
    
    setFilteredTitles(result);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(titles, query, filterType);
  };

  const handleFilterType = (type) => {
    setFilterType(type);
    applyFilters(titles, searchQuery, type);
  };

  useEffect(() => {
    loadTitles();
  }, []);

  const handleEnrichFromIMDb = async () => {
    try {
      const data = { imdb_id: imdbId };
      if (parentId) data.parent_id = parentId;
      
      console.log('Enriching with data:', data);
      const response = await titleAPI.enrichFromIMDb(data);
      console.log('Enrich response:', response);
      
      setShowEnrichModal(false);
      setImdbId('');
      setParentId('');
      loadTitles();
    } catch (error) {
      console.error('Enrich error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setError(`Failed to enrich from IMDb: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleBulkDelete = async (cascade = false) => {
    if (selectedItems.length === 0) return;
    
    try {
      setLoading(true);
      const deletePromises = selectedItems.map(item => 
        titleAPI.deleteTitle(item.title_id, cascade)
      );
      
      await Promise.all(deletePromises);
      setSelectedItems([]);
      loadTitles();
    } catch (error) {
      setError(`Failed to delete ${selectedItems.length} titles`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      id: 'poster',
      header: 'Poster',
      cell: item => (
        <Box>
          {item.metadata?.poster_url ? (
            <img 
              src={item.metadata.poster_url} 
              alt={item.metadata.title}
              style={{ width: '50px', height: '75px', objectFit: 'cover' }}
            />
          ) : (
            <Box textAlign="center" color="text-status-inactive">
              No Image
            </Box>
          )}
        </Box>
      ),
      width: 80
    },
    {
      id: 'title',
      header: 'Title',
      cell: item => (
        <Button 
          variant="link" 
          onClick={() => navigate(`/titles/${item.title_id}`)}
        >
          {item.metadata?.title || item.title_id}
        </Button>
      )
    },
    {
      id: 'type',
      header: 'Type',
      cell: item => <Badge>{item.content_type}</Badge>
    },
    {
      id: 'year',
      header: 'Year',
      cell: item => item.metadata?.year || '-'
    },
    {
      id: 'genre',
      header: 'Genre',
      cell: item => item.metadata?.genre?.join(', ') || '-'
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => <Badge color="blue">{item.metadata?.status || 'draft'}</Badge>
    },
    {
      id: 'imdb_rating',
      header: 'IMDb Rating',
      cell: item => item.metadata?.imdb_rating ? `⭐ ${item.metadata.imdb_rating}` : '-'
    }
  ];

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Toggle
                checked={hierarchicalView}
                onChange={({ detail }) => setHierarchicalView(detail.checked)}
              >
                Hierarchical View
              </Toggle>
              <Button onClick={() => setShowEnrichModal(true)}>
                Enrich from IMDb
              </Button>
              <Button 
                variant="primary" 
                onClick={() => navigate('/create')}
              >
                Create Title
              </Button>
            </SpaceBetween>
          }
        >
          Titles ({filteredTitles.length}{searchQuery || (filterType && filterType.value) ? ` of ${titles.length}` : ''})
        </Header>
      }
    >
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <Container>
        <SpaceBetween direction="horizontal" size="m">
          <div style={{ flex: 1, minWidth: '300px' }}>
            <Input
              type="search"
              placeholder="Search titles, descriptions, genres..."
              value={searchQuery}
              onChange={({ detail }) => handleSearch(detail.value)}
            />
          </div>
          <div style={{ minWidth: '180px' }}>
            <Select
              selectedOption={filterType}
              onChange={({ detail }) => handleFilterType(detail.selectedOption)}
              options={contentTypeOptions}
              placeholder="Filter by type"
            />
          </div>
        </SpaceBetween>
      </Container>
      
      {!hierarchicalView && selectedItems.length > 0 && (
        <Alert type="info">
          {selectedItems.length} item(s) selected
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                variant="normal"
                onClick={() => handleBulkDelete(false)}
              >
                Delete Selected
              </Button>
              <Button 
                variant="primary"
                onClick={() => handleBulkDelete(true)}
              >
                Delete Selected + Children
              </Button>
            </SpaceBetween>
          </Box>
        </Alert>
      )}
      
      {hierarchicalView ? (
        <HierarchicalTitleView titles={filteredTitles} />
      ) : (
        <Table
          columnDefinitions={columns}
          items={filteredTitles}
          loading={loading}
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectionType="multi"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No titles</b>
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                No titles to display.
              </Box>
              <Button onClick={() => navigate('/create')}>Create title</Button>
            </Box>
          }
        />
      )}

      <Modal
        visible={showEnrichModal}
        onDismiss={() => setShowEnrichModal(false)}
        header="Enrich from IMDb"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowEnrichModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleEnrichFromIMDb}
                disabled={!imdbId}
              >
                Enrich
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label={<span style={{textTransform: 'none'}}>IMDb ID</span>} description="Format: tt1234567">
            <Input
              value={imdbId}
              onChange={({ detail }) => setImdbId(detail.value)}
              placeholder="tt1234567"
            />
          </FormField>
          <FormField 
            label="Parent ID (Optional)" 
            description="Required for episodes - link to season"
          >
            <Input
              value={parentId}
              onChange={({ detail }) => setParentId(detail.value)}
              placeholder="abc123"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
};

export default TitleList;